import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { getInvoicesForCompany, downloadInvoicePdf, uploadInvoicePdf, deleteInvoicePdf, type Invoice, uploadCommissionInvoice } from '../services/invoiceService';
import { getOrderById, type Order } from '../services/orderService';
import { formatCurrency } from '../utils/currency';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Download, Search, Eye, FileText, X, Store, Upload, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const statusTranslations: Record<string, string> = {
  'faturado': 'Faturado',
  'incompleto': 'Incompleto',
  'por_faturar': 'Por Faturar'
};

const statusColors: Record<string, string> = {
  'faturado': 'text-green-600',
  'incompleto': 'text-yellow-600',
  'por_faturar': 'text-red-600'
};

interface InvoiceModalProps {
  invoice: Invoice;
  onClose: () => void;
  onRefresh: () => void;
}

interface OrderModalProps {
  orderId: string;
  onClose: () => void;
}

function OrderModal({ orderId, onClose }: OrderModalProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const orderData = await getOrderById(orderId);
        setOrder(orderData);
      } catch (err) {
        console.error('Error loading order:', err);
        setError('Erro ao carregar encomenda');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl h-[90vh] p-6">
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl h-[90vh] p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-semibold text-red-600">
              Erro ao carregar encomenda
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-gray-600">{error || 'Encomenda não encontrada'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl h-[90vh] p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-primary mb-2">
              Encomenda #{order.orderNumber}
            </h2>
            <p className="text-gray-500">
              {format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm', { locale: pt })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informações do Cliente
            </h3>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600">Nome</div>
                  <div className="mt-1">
                    {order.shippingAddress?.name || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">NIF</div>
                  <div className="mt-1">
                    {order.shippingAddress?.taxNumber || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Email</div>
                  <div className="mt-1">
                    {order.shippingAddress?.email || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Telefone</div>
                  <div className="mt-1">
                    {order.shippingAddress?.phone || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Morada</div>
                  <div className="mt-1">
                    {order.shippingAddress?.street ? 
                      `${order.shippingAddress.street}${order.shippingAddress.number ? `, ${order.shippingAddress.number}` : ''}${order.shippingAddress.complement ? `, ${order.shippingAddress.complement}` : ''}` 
                      : 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Cidade</div>
                  <div className="mt-1">
                    {order.shippingAddress?.city || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Código Postal</div>
                  <div className="mt-1">
                    {order.shippingAddress?.postalCode || 'N/A'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">País</div>
                  <div className="mt-1">
                    {order.shippingAddress?.country || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Resumo da Encomenda
            </h3>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-100">
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Subtotal</dt>
                  <dd className="text-gray-900">{formatCurrency(order.subtotal)}</dd>
                </div>
                {order.shippingCost > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Portes</dt>
                    <dd className="text-gray-900">{formatCurrency(order.shippingCost)}</dd>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-200">
                  <dt className="text-sm font-medium text-gray-900">Total</dt>
                  <dd className="text-lg font-semibold text-primary">{formatCurrency(order.total)}</dd>
                </div>
                {order.commission && order.commission > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-500">Comissão</dt>
                    <dd className="text-gray-900">{formatCurrency(order.commission)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Products */}
          <div className="col-span-full mt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Produtos</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produto
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantidade
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {item.mainImage && (
                            <img
                              src={item.mainImage}
                              alt={item.name}
                              className="h-12 w-12 rounded-lg object-cover mr-4 border border-gray-200"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            {item.sku && (
                              <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatCurrency(item.price)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-primary">
                        {formatCurrency(item.price * item.quantity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoiceModal({ invoice, onClose, onRefresh }: InvoiceModalProps) {
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUpload = async (type: 'client' | 'commission', sellerId?: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const pdfUrl = await uploadInvoicePdf(invoice.orderId, file, type, sellerId);
          setSuccess('Fatura enviada com sucesso!');
          
          // Atualizar o estado local sem recarregar
          if (type === 'client') {
            invoice.pdfUrl = pdfUrl;
            invoice.status = invoice.sellers.every(s => s.commissionPdfUrl) ? 'faturado' : 'incompleto';
          } else if (type === 'commission' && sellerId) {
            invoice.sellers = invoice.sellers.map(seller => 
              seller.id === sellerId 
                ? { ...seller, commissionPdfUrl: pdfUrl }
                : seller
            );
            
            const allSellersHaveInvoice = invoice.sellers.every(s => s.commissionPdfUrl);
            invoice.status = invoice.pdfUrl && allSellersHaveInvoice ? 'faturado' : 'incompleto';
          }
        } catch (err) {
          console.error('Error uploading PDF:', err);
          setError('Erro ao enviar o PDF');
        }
      }
    };
    
    input.click();
  };

  const handleViewPdf = (url: string) => {
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    setSelectedPdfUrl(viewerUrl);
  };

  const handleClosePdf = () => {
    setSelectedPdfUrl(null);
  };

  const handleDelete = async (type: 'client' | 'commission', sellerId?: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta fatura?')) {
      try {
        await deleteInvoicePdf(invoice.orderId, type, sellerId);
        setSuccess('Fatura excluída com sucesso!');
        
        // Atualizar o estado local sem recarregar tudo
        if (type === 'client') {
          invoice.pdfUrl = null;
          invoice.status = 'por_faturar';
        } else if (type === 'commission' && sellerId) {
          invoice.sellers = invoice.sellers.map(seller => 
            seller.id === sellerId 
              ? { ...seller, commissionPdfUrl: null }
              : seller
          );
        }
      } catch (err) {
        console.error('Error deleting PDF:', err);
        setError('Erro ao excluir o PDF');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* PDF Viewer Modal */}
        {selectedPdfUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium">Visualizar Fatura</h3>
                <button
                  onClick={handleClosePdf}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 w-full h-full min-h-0">
                <iframe
                  src={selectedPdfUrl}
                  className="w-full h-full border-none"
                  title="PDF Viewer"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}

        {/* Modal Header */}
        <div className="flex justify-between items-start p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Faturas da Encomenda {invoice.orderNumber}
            </h2>
            <div className="mt-1 flex items-center">
              <span className="text-sm text-gray-500 mr-2">Estado:</span>
              <span className={statusColors[invoice.status]}>
                {statusTranslations[invoice.status]}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Client Invoice */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Fatura do Cliente
                </h3>
                <span className={`px-2 py-1 text-sm font-medium rounded-full ${
                  invoice.pdfUrl ? 'text-green-700 bg-green-100' : 'text-yellow-700 bg-yellow-100'
                }`}>
                  {invoice.pdfUrl ? 'FATURADO' : 'NAO FATURADO'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {invoice.pdfUrl ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewPdf(invoice.pdfUrl)}
                      className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                      title="Ver Fatura"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete('client')}
                      className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                      title="Excluir Fatura"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleUpload('client')}
                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                    title="Upload Fatura"
                  >
                    <Upload className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Commission Invoices */}
          {invoice.sellers && invoice.sellers.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Faturas de Comissão
              </h3>
              {invoice.sellers.map((seller, index) => {
                const hasInvoice = seller.commissionPdfUrl;
                
                return (
                  <div key={`seller-${seller.id || index}`} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium">
                            {seller.name} (NIF {seller.vat})
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            hasInvoice ? 'text-green-700 bg-green-100' : 'text-yellow-700 bg-yellow-100'
                          }`}>
                            {hasInvoice ? 'FATURADO' : 'NAO FATURADO'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Comissão: {formatCurrency(seller.commission)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasInvoice ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewPdf(seller.commissionPdfUrl)}
                              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                              title="Ver Fatura"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete('commission', seller.id)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                              title="Excluir Fatura"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleUpload('commission', seller.id)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                            title="Upload Fatura"
                          >
                            <Upload className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Treasury() {
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      loadInvoices();
    }
  }, [currentUser]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getInvoicesForCompany(currentUser?.role === 'admin' ? null : currentUser!.companyId);
      setInvoices(data);
    } catch (err) {
      setError('Erro ao carregar faturas');
      console.error('Error loading invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => 
    invoice.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.sellers.some(seller => seller.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Tesouraria</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Pesquisar faturas..."
            className="pl-10 pr-4 py-2 border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">A carregar faturas...</div>
      ) : error ? (
        <div key="error-message" className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nº Encomenda
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Nenhuma fatura encontrada
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.orderNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.buyerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.sellers.length === 1 ? 
                        `${invoice.sellers[0].name} (NIF ${invoice.sellers[0].vat})` 
                        : `${invoice.sellers.length} vendedores`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {invoice.isOnlineSale ? (
                        <div className="inline-flex items-center gap-1 text-blue-600">
                          <Store className="h-4 w-4" />
                          <span>Marketplace</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">Loja</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(invoice.createdAt), 'dd/MM/yyyy', { locale: pt })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={statusColors[invoice.status]}>
                        {statusTranslations[invoice.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => setSelectedOrderId(invoice.orderId)}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        title="Ver pedido"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        title="Ver faturas"
                      >
                        <FileText className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onRefresh={loadInvoices}
        />
      )}

      {selectedOrderId && (
        <OrderModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </div>
  );
}